import { AfterViewInit, Component, Input, OnInit, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { ModalService } from '../../core/services/modal.service';
import { AlertService } from '../../core/services/alert.service';
import { UserService } from '../../core/services/user.service';

export interface User {
    name: string;
    email: string;
    role: string;
    addedBy: string;
    timeAdded: string;
}

@Component({
    selector: 'app-admin-role-table',
    templateUrl: './admin-role-table.component.html',
    styleUrls: ['./admin-role-table.component.scss'],
})
export class AdminRoleTableComponent implements OnInit, AfterViewInit {
    @Input() users: User[] = [];
    @Input() groupName: string = '';

    displayedColumns: string[] = ['user', 'addedBy', 'timeAdded', 'actions'];
    dataSource = new MatTableDataSource<User>();

    @ViewChild(MatSort) sort: MatSort;

    newUserEmail = '';
    hasEmailError = false;
    emailErrorMessage = '';

    sortedColumn: string | null = null;

    constructor(
        private modal: ModalService,
        private alert: AlertService,
        private userService: UserService,
    ) {}

    ngOnInit() {
        this.dataSource = new MatTableDataSource(this.users);
    }

    ngAfterViewInit() {
        this.dataSource.sort = this.sort;

        this.sort.sortChange.subscribe((sortState) => {
            this.sortedColumn = sortState.direction ? sortState.active : null;
        });
    }

    get existingEmails(): string[] {
        return this.users.map((user) => user.email.toLowerCase());
    }

    onEmailInput(email: string): void {
        this.newUserEmail = email;

        if (this.existingEmails?.includes(email.trim().toLowerCase())) {
            this.hasEmailError = true;
            this.emailErrorMessage = 'This address has already been added to this group.';
            return;
        }

        this.hasEmailError = false;
        this.emailErrorMessage = '';
    }

    addUser(): void {
        if (this.hasEmailError || !this.newUserEmail) {
            return;
        }

        if (this.existingEmails.includes(this.newUserEmail.trim().toLowerCase())) {
            this.hasEmailError = true;
            this.emailErrorMessage = 'This address has already been added to this group.';
            return;
        }

        if (!this.newUserEmail.toLowerCase().includes('@srsacquiom.com')) {
            this.hasEmailError = true;
            this.emailErrorMessage = 'Only @srsacquiom.com domain emails can be added.';
            return;
        }

        const currentUserName = this.userService.user.name;

        const newUser: User = {
            name: 'New User',
            email: this.newUserEmail.trim(),
            role: 'Role Placeholder',
            addedBy: currentUserName,
            timeAdded: new Date().toLocaleString(),
        };

        this.users.push(newUser);
        this.dataSource.data = [...this.users];
        this.newUserEmail = '';
        this.hasEmailError = false;
        this.emailErrorMessage = '';

        this.alert.show({
            title: '',
            description: `<strong>${newUser.email}</strong> has been added to <strong>${this.groupName}</strong>.`,
            connotation: 'success',
            icon: 'check_circle',
        });
    }

    async deleteUser(user: User): Promise<void> {
        const result = await this.modal.confirm({
            title: 'Remove User',
            description: `This will remove <strong>${user.name}</strong> from <strong>${this.groupName}</strong>?`,
            confirmText: 'Remove',
            cancelText: 'Cancel',
        });

        if (result === 'confirm') {
            this.users = this.users.filter((u) => u !== user);
            this.dataSource.data = [...this.users];

            this.alert.show({
                title: '',
                description: `<strong>${user.name}</strong> has been successfully removed from <strong>${this.groupName}</strong>.`,
                connotation: 'success',
                icon: 'check_circle',
            });
        }
    }
}
